/**
 * @fileoverview Provide controlled identity/licensing/contact inputs for an email signature draft. The parent owns form state; shared signature utilities own generated HTML and clipboard behavior. Editing these fields does not update canonical employee records or email-provider settings.
 */

import React, { useContext } from "react";
import Button from "../../shared/AppButton";
import { InputText } from "../../shared/AppTextInput";
import { InputTextarea } from "../../shared/AppTextarea";
import { Checkbox } from "../../shared/AppCheckbox";
import { Divider } from "../../shared/AppDivider";
import {
  copyToClipboard,
  generateSignatureHtml } from
"../../../lib/signature-utils";
import ToastContext from "../../../ToastContext";



export default function SignatureForm({ onDataChange, formData }) {
  const showToast = useContext(ToastContext);

  const handleChange =

  (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    onDataChange({ ...formData, [name]: newValue });
  };

  const handleLicenseChange =

  (index, e) => {
    const { name, value } = e.target;
    const newLicenseStates = [...formData.licenseStates];
    newLicenseStates[index][name] = value;
    onDataChange({ ...formData, licenseStates: newLicenseStates });
  };

  const addLicenseState =

  () => {
    onDataChange({
      ...formData,
      licenseStates: [...formData.licenseStates, { state: "", barNumber: "" }]
    });
  };

  const removeLicenseState =

  (index) => {
    const newLicenseStates = formData.licenseStates.filter((_, i) => i !== index);
    onDataChange({ ...formData, licenseStates: newLicenseStates });
  };

  const handleCopySignature =

  async () => {
    const html = generateSignatureHtml(formData);
    const success = await copyToClipboard(html);

    if (success) {
      showToast({
        severity: "success",
        summary: "Success!",
        detail: "Signature copied to clipboard successfully!"
      });
    } else {
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Failed to copy signature. Please try again."
      });
    }
  };

  const handleReset =

  () => {
    onDataChange({
      name: "",
      title: "",
      isAttorney: false,
      licenseStates: [],
      officePhone: "",
      mobilePhone: "",
      email: "",
      address: ""
    });
  };

  const formatPhoneNumber =

  (value) => {
    const digits = value.replace(/\\D/g, "");
    if (digits.length >= 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
        6,
        10
      )}`;
    }
    return value;
  };

  const handlePhoneChange =

  (e) => {
    const { name, value } = e.target;
    onDataChange({ ...formData, [name]: formatPhoneNumber(value) });
  };

  return (
    <div>
      <div className="text-xl font-semibold text-900 mb-4">Personal Information</div>
      <div className="p-fluid">
        <div className="field grid">
          <label htmlFor="name" className="col-12 mb-2 md:col-2 md:mb-0">Full Name *</label>
          <div className="col-12 md:col-10">
            <InputText
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name" />

          </div>
        </div>

        <div className="field grid">
          <label htmlFor="title" className="col-12 mb-2 md:col-2 md:mb-0">Title</label>
          <div className="col-12 md:col-10">
            <InputText
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Partner, Associate, Office Manager" />

          </div>
        </div>

        <Divider />

        <div className="field-checkbox">
          <Checkbox
            inputId="isAttorney"
            name="isAttorney"
            checked={formData.isAttorney}
            onChange={handleChange} />

          <label htmlFor="isAttorney" className="ml-2">
            Attorney
          </label>
        </div>

        {formData.isAttorney &&
        <div className="mt-4">
            <div className="flex justify-content-between align-items-center mb-3">
              <h3 className="text-lg font-medium m-0">License Information</h3>
              <Button
              icon="pi pi-plus"
              label="Add State"
              onClick={addLicenseState}
              className="p-button-sm p-button-outlined" />

            </div>

            {formData.licenseStates.map((license, index) =>
          <div key={index} className="grid formgrid p-fluid align-items-end mb-3">
                <div className="field col-12 md:col-5">
                   <label htmlFor={`license_state_${index}`}>State</label>
                  <InputText
                id={`license_state_${index}`}
                name="state"
                value={license.state}
                onChange={(e) => handleLicenseChange(index, e)}
                placeholder="State" />

                </div>
                <div className="field col-12 md:col-5">
                  <label htmlFor={`license_bar_${index}`}>Bar Number</label>
                  <InputText
                id={`license_bar_${index}`}
                name="barNumber"
                value={license.barNumber}
                onChange={(e) => handleLicenseChange(index, e)}
                placeholder="Bar Number" />

                </div>
                <div className="field col-12 md:col-2">
                  <Button
                icon="pi pi-trash"
                onClick={() => removeLicenseState(index)}
                className="p-button-danger p-button-outlined w-full" />

                </div>
              </div>
          )}
          </div>}


        <Divider />

        <h3 className="text-lg font-medium mb-3">Contact Information</h3>

        <div className="formgrid grid">
            <div className="field col-12 md:col-6">
                <label htmlFor="officePhone">Office Phone</label>
                <InputText
              id="officePhone"
              name="officePhone"
              value={formData.officePhone}
              onChange={handlePhoneChange}
              placeholder="(657) 845-3100" />

            </div>
            <div className="field col-12 md:col-6">
                <label htmlFor="mobilePhone">Mobile Phone</label>
                <InputText
              id="mobilePhone"
              name="mobilePhone"
              value={formData.mobilePhone}
              onChange={handlePhoneChange}
              placeholder="(202) 321-9999" />

            </div>
        </div>

        <div className="field">
          <label htmlFor="email">Email Address *</label>
          <InputText
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your.name@kneuppercovey.com" />

        </div>

        <div className="field">
          <label htmlFor="address">Mailing Address</label>
          <InputTextarea
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="11720 Amber Park Dr, Ste 160, PMB 1271, Alpharetta, GA 30009"
            rows={3} />

        </div>

        <Divider />

        <div className="grid">
            <div className="col-12 md:col-6">
                <Button
              label="Copy HTML Signature"
              icon="pi pi-copy"
              onClick={handleCopySignature}
              className="w-full" />

            </div>
            <div className="col-12 md:col-6">
                <Button
              label="Reset Form"
              icon="pi pi-refresh"
              onClick={handleReset}
              className="p-button-outlined w-full" />

            </div>
        </div>
      </div>
    </div>);

}
